import {
	CandidateDisabledReason,
	courseRequiresForm,
	courseRequiresShakespeare,
	CourseSelections,
	formatWorkLabel,
	getCandidateAvailabilityInSlot,
	sanitizeTitleInlineHtml,
	SELECT_PLACEHOLDER_TEXT,
	stripTitleHtml,
} from '@/components/prescriptions/utils/prescriptionSelectionRules'
import type {
	PrescriptionSelectionCourse,
	PrescriptionSelectionFocusAreaSelector,
	PrescriptionSelectionGroup,
	PrescriptionSelectionWork,
} from '@/types/fetchPrescriptionSelection'
import { Icon } from '@iconify/react'
import {
	Fragment,
	KeyboardEvent as ReactKeyboardEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

export interface PrescriptionWorkSelectorProps {
	course: PrescriptionSelectionCourse
	selector: PrescriptionSelectionFocusAreaSelector
	slotIndex: number
	courseSelections: CourseSelections | undefined
	onChange: (_workIds: string[]) => void
	htmlId: string
	ariaLabel?: string
}

const buildOptionValue = (workIds: string[]): string => workIds.join(',')

const buildSingleLineLabel = (work: PrescriptionSelectionWork): string =>
	formatWorkLabel(work.author, stripTitleHtml(work.title))

const buildBundleLabel = (works: PrescriptionSelectionWork[]): string =>
	works.map(buildSingleLineLabel).join(' AND ')

interface OptionRow {
	value: string
	label: string
	workIds: string[]
	isBundle: boolean
	disabled: boolean
	// Why the row is disabled — surfaced as a `data-disabled-reason` attribute
	// for debugging only (not shown in the UI).
	disabledReason?: CandidateDisabledReason
	work?: PrescriptionSelectionWork
	bundleWorks?: PrescriptionSelectionWork[]
	showTypePill: boolean
}

type DisplayItem =
	| { kind: 'header'; key: string; groupTitle: string }
	| { kind: 'option'; key: string; row: OptionRow; flatIndex: number }

const groupHasMixedTypes = (group: PrescriptionSelectionGroup): boolean => {
	const works = group.works ?? []
	if (works.length < 2) return false
	const types = new Set<string>()
	for (const w of works) types.add(w['prescription-type'])
	return types.size > 1
}

// Flattened work count: standalone works + bundle inner works.
const groupFlattenedWorkCount = (group: PrescriptionSelectionGroup): number =>
	(group.works?.length ?? 0) +
	(group.bundles ?? []).reduce((n, b) => n + (b.works?.length ?? 0), 0)

// Whether plain-work options in a group render the `prescription-type` pill:
// mixed-type group OR a single-work group (single-work exception).
// Bundles never show a type pill regardless.
const shouldShowGroupTypePill = (group: PrescriptionSelectionGroup): boolean =>
	groupHasMixedTypes(group) || groupFlattenedWorkCount(group) === 1

const buildOptionsForGroup = (
	group: PrescriptionSelectionGroup,
	course: PrescriptionSelectionCourse,
	slotIndex: number,
	courseSelections: CourseSelections | undefined,
): OptionRow[] => {
	const rows: OptionRow[] = []
	const showTypePillForGroup = shouldShowGroupTypePill(group)
	;(group.works ?? []).forEach((work) => {
		const availability = getCandidateAvailabilityInSlot(
			[work.id],
			slotIndex,
			course,
			courseSelections,
		)
		rows.push({
			value: buildOptionValue([work.id]),
			label: buildSingleLineLabel(work),
			workIds: [work.id],
			isBundle: false,
			disabled: availability.disabled,
			disabledReason: availability.reason,
			work,
			showTypePill: showTypePillForGroup,
		})
	})
	;(group.bundles ?? []).forEach((bundle) => {
		const works = bundle.works ?? []
		if (works.length === 0) return
		const availability = getCandidateAvailabilityInSlot(
			works.map((w) => w.id),
			slotIndex,
			course,
			courseSelections,
		)
		rows.push({
			value: buildOptionValue(works.map((w) => w.id)),
			label: buildBundleLabel(works),
			workIds: works.map((w) => w.id),
			isBundle: true,
			disabled: availability.disabled,
			disabledReason: availability.reason,
			bundleWorks: works,
			showTypePill: false,
		})
	})
	return rows
}

const findFirstEnabledIndex = (rows: OptionRow[]): number =>
	rows.findIndex((r) => !r.disabled)

const findLastEnabledIndex = (rows: OptionRow[]): number => {
	for (let i = rows.length - 1; i >= 0; i--) {
		if (!rows[i].disabled) return i
	}
	return -1
}

const findNextEnabledIndex = (rows: OptionRow[], from: number): number => {
	if (rows.length === 0) return -1
	for (let step = 1; step <= rows.length; step++) {
		const idx = (from + step + rows.length) % rows.length
		if (!rows[idx].disabled) return idx
	}
	return -1
}

const findPrevEnabledIndex = (rows: OptionRow[], from: number): number => {
	if (rows.length === 0) return -1
	for (let step = 1; step <= rows.length; step++) {
		const idx = (from - step + rows.length) % rows.length
		if (!rows[idx].disabled) return idx
	}
	return -1
}

export function PrescriptionWorkSelector({
	course,
	selector,
	slotIndex,
	courseSelections,
	onChange,
	htmlId,
	ariaLabel,
}: PrescriptionWorkSelectorProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const listboxRef = useRef<HTMLUListElement>(null)

	const [isOpen, setIsOpen] = useState(false)
	const [highlightedIndex, setHighlightedIndex] = useState(-1)

	const groupedOptions = useMemo(() => {
		const groups = selector.groups ?? []
		return groups.map((group, gIdx) => ({
			key: `${group['group-title']}-${gIdx}`,
			groupTitle: group['group-title'],
			rows: buildOptionsForGroup(
				group,
				course,
				slotIndex,
				courseSelections,
			),
		}))
	}, [selector, course, slotIndex, courseSelections])

	const flatRows = useMemo(
		() => groupedOptions.flatMap((g) => g.rows),
		[groupedOptions],
	)

	const courseHasShakespeareRule = useMemo(
		() => courseRequiresShakespeare(course),
		[course],
	)

	const courseShowsFormPill = useMemo(
		() => courseRequiresForm(course),
		[course],
	)

	const displayItems = useMemo<DisplayItem[]>(() => {
		const items: DisplayItem[] = []
		let counter = 0
		groupedOptions.forEach((group) => {
			items.push({
				kind: 'header',
				key: `h-${group.key}`,
				groupTitle: group.groupTitle,
			})
			group.rows.forEach((row) => {
				items.push({
					kind: 'option',
					key: `o-${row.value}-${counter}`,
					row,
					flatIndex: counter,
				})
				counter++
			})
		})
		return items
	}, [groupedOptions])

	const currentSlot = courseSelections?.[slotIndex]
	const currentValue = currentSlot
		? buildOptionValue(currentSlot.workIds)
		: ''
	const currentRow = flatRows.find((r) => r.value === currentValue)

	const triggerLabel = currentRow?.label ?? SELECT_PLACEHOLDER_TEXT
	const isPlaceholder = !currentRow

	const listboxId = `${htmlId}-listbox`
	const activeOptionId =
		isOpen && highlightedIndex >= 0
			? `${htmlId}-opt-${highlightedIndex}`
			: undefined

	const openPopover = (initialIdx?: number) => {
		const candidate =
			typeof initialIdx === 'number'
				? initialIdx
				: currentRow
				? flatRows.indexOf(currentRow)
				: findFirstEnabledIndex(flatRows)
		const safeIdx =
			candidate >= 0 && !flatRows[candidate]?.disabled
				? candidate
				: findFirstEnabledIndex(flatRows)
		setHighlightedIndex(safeIdx)
		setIsOpen(true)
	}

	const closePopover = (returnFocus = true) => {
		setIsOpen(false)
		if (returnFocus) triggerRef.current?.focus()
	}

	const selectRow = (row: OptionRow) => {
		if (row.disabled) return
		onChange(row.workIds)
		closePopover()
	}

	const handleTriggerClick = () => {
		if (isOpen) {
			closePopover(false)
		} else {
			openPopover()
		}
	}

	const handleTriggerKeyDown = (
		event: ReactKeyboardEvent<HTMLButtonElement>,
	) => {
		if (
			event.key === 'Enter' ||
			event.key === ' ' ||
			event.key === 'ArrowDown'
		) {
			event.preventDefault()
			openPopover(findFirstEnabledIndex(flatRows))
		} else if (event.key === 'ArrowUp') {
			event.preventDefault()
			openPopover(findLastEnabledIndex(flatRows))
		}
	}

	const handleListboxKeyDown = (
		event: ReactKeyboardEvent<HTMLUListElement>,
	) => {
		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault()
				setHighlightedIndex((idx) =>
					findNextEnabledIndex(flatRows, idx),
				)
				break
			}
			case 'ArrowUp': {
				event.preventDefault()
				setHighlightedIndex((idx) =>
					findPrevEnabledIndex(flatRows, idx),
				)
				break
			}
			case 'Home': {
				event.preventDefault()
				setHighlightedIndex(findFirstEnabledIndex(flatRows))
				break
			}
			case 'End': {
				event.preventDefault()
				setHighlightedIndex(findLastEnabledIndex(flatRows))
				break
			}
			case 'Enter':
			case ' ': {
				event.preventDefault()
				const target = flatRows[highlightedIndex]
				if (target) selectRow(target)
				break
			}
			case 'Escape': {
				event.preventDefault()
				closePopover()
				break
			}
			case 'Tab': {
				closePopover(false)
				break
			}
			default:
				break
		}
	}

	useEffect(() => {
		if (!isOpen) return
		listboxRef.current?.focus()
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		const handleMouseDown = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleMouseDown)
		return () => document.removeEventListener('mousedown', handleMouseDown)
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		if (highlightedIndex < 0) return
		const node = listboxRef.current?.querySelector<HTMLLIElement>(
			`[data-flat-index="${highlightedIndex}"]`,
		)
		node?.scrollIntoView({ block: 'nearest' })
	}, [isOpen, highlightedIndex])

	return (
		<div ref={containerRef} className="relative w-full">
			<button
				ref={triggerRef}
				id={htmlId}
				type="button"
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={listboxId}
				aria-label={ariaLabel}
				aria-activedescendant={activeOptionId}
				onClick={handleTriggerClick}
				onKeyDown={handleTriggerKeyDown}
				className={[
					'w-full min-h-12 flex items-start justify-between gap-2',
					'px-3 py-2 border border-nsw-grey-04 bg-white text-left',
					'focus:outline focus:outline-3 focus:outline-[color:var(--nsw-focus)]',
					isPlaceholder ? 'text-nsw-grey-02' : 'text-nsw-grey-01',
				].join(' ')}
				data-testid={htmlId}
			>
				<span className="flex-1 min-w-0">
					{isPlaceholder ? (
						triggerLabel
					) : currentRow?.isBundle && currentRow.bundleWorks ? (
						<BundleOptionContent works={currentRow.bundleWorks} />
					) : currentRow?.work ? (
						<PlainWorkOptionContent work={currentRow.work} />
					) : (
						triggerLabel
					)}
				</span>
				<Icon
					icon="ic:baseline-keyboard-arrow-down"
					width={24}
					height={24}
					aria-hidden="true"
				/>
			</button>

			{isOpen && (
				<ul
					ref={listboxRef}
					id={listboxId}
					role="listbox"
					tabIndex={-1}
					aria-label={ariaLabel}
					aria-activedescendant={activeOptionId}
					onKeyDown={handleListboxKeyDown}
					className={[
						'absolute z-30 left-0 right-0 top-full mt-1',
						'max-h-96 overflow-y-auto',
						'bg-white border border-nsw-grey-04 shadow-lg',
						'focus:outline-none',
					].join(' ')}
				>
					{displayItems.map((item) => {
						if (item.kind === 'header') {
							return (
								<li
									key={item.key}
									role="presentation"
									className="px-3 pt-3 pb-1 text-base font-bold text-nsw-brand-dark"
								>
									{item.groupTitle}
								</li>
							)
						}
						const { row, flatIndex } = item
						const isHighlighted = flatIndex === highlightedIndex
						const isSelected = row.value === currentValue
						const hasTypePill = row.showTypePill && !!row.work
						const hasFormPill =
							courseShowsFormPill &&
							!!row.work &&
							row.work['prescription-form'] != null
						const hasShakespearePill =
							courseHasShakespeareRule &&
							(row.work?.shakespeare === true ||
								row.bundleWorks?.some((w) => w.shakespeare) ===
									true)
						return (
							<li
								key={item.key}
								id={`${htmlId}-opt-${flatIndex}`}
								role="option"
								aria-selected={isSelected}
								aria-disabled={row.disabled}
								data-flat-index={flatIndex}
								data-disabled-reason={row.disabledReason}
								onMouseEnter={() => {
									if (!row.disabled)
										setHighlightedIndex(flatIndex)
								}}
								onClick={() => selectRow(row)}
								className={[
									'flex items-start gap-3 pl-9 pr-3 py-2',
									row.disabled
										? 'cursor-not-allowed text-[#767676]'
										: 'cursor-pointer text-nsw-grey-01',
									isHighlighted && !row.disabled
										? 'bg-nsw-off-white'
										: '',
									isSelected ? 'font-bold' : '',
								].join(' ')}
							>
								<div className="flex-1 min-w-0">
									{row.isBundle && row.bundleWorks ? (
										<BundleOptionContent
											works={row.bundleWorks}
											hidePublicationDetails
										/>
									) : row.work ? (
										<PlainWorkOptionContent
											work={row.work}
											hidePublicationDetails
										/>
									) : (
										<span className="block">
											{row.label}
										</span>
									)}
								</div>
								{(hasTypePill ||
									hasFormPill ||
									hasShakespearePill) && (
									<div className="shrink-0 flex gap-1">
										{hasTypePill && row.work && (
											<span className="inline-block px-2 py-1 text-xs font-bold rounded bg-nsw-off-white text-nsw-grey-01 border border-nsw-grey-04">
												{row.work[
													'prescription-type'
												].toLowerCase()}
											</span>
										)}
										{hasFormPill &&
											row.work &&
											row.work['prescription-form'] && (
												<span className="inline-block px-2 py-1 text-xs font-bold rounded bg-nsw-off-white text-nsw-grey-01 border border-nsw-grey-04">
													{row.work[
														'prescription-form'
													].toLowerCase()}
												</span>
											)}
										{hasShakespearePill && (
											<span className="inline-block px-2 py-1 text-xs font-bold rounded bg-nsw-off-white text-nsw-grey-01 border border-nsw-grey-04">
												Shakespeare
											</span>
										)}
									</div>
								)}
							</li>
						)
					})}
				</ul>
			)}
		</div>
	)
}

interface PlainWorkOptionContentProps {
	work: PrescriptionSelectionWork
	hidePublicationDetails?: boolean
}

function PlainWorkOptionContent({
	work,
	hidePublicationDetails = false,
}: PlainWorkOptionContentProps) {
	// Preserve the title's inline emphasis (e.g. an italicised phrase) instead
	// of stripping it. Author stays plain; title is sanitised HTML.
	const titleHtml = sanitizeTitleInlineHtml(work.title)
	const publicationDetail = work.publicationdetails
	const subtitle = work.subtitle

	return (
		<Fragment>
			<span className="block">
				{work.author}
				{work.author && titleHtml && ', '}
				{titleHtml && (
					<span dangerouslySetInnerHTML={{ __html: titleHtml }} />
				)}
			</span>
			{publicationDetail && !hidePublicationDetails && (
				<span className="block text-sm text-nsw-grey-02">
					{publicationDetail}
				</span>
			)}
			{subtitle && <span className="block text-sm pl-4">{subtitle}</span>}
		</Fragment>
	)
}

interface BundleOptionContentProps {
	works: PrescriptionSelectionWork[]
	hidePublicationDetails?: boolean
}

function BundleOptionContent({
	works,
	hidePublicationDetails = false,
}: BundleOptionContentProps) {
	return (
		<Fragment>
			{works.map((work, index) => (
				<Fragment key={work.id}>
					{index > 0 && <span className="block">AND</span>}
					<PlainWorkOptionContent
						work={work}
						hidePublicationDetails={hidePublicationDetails}
					/>
				</Fragment>
			))}
		</Fragment>
	)
}

export default PrescriptionWorkSelector
